'use client';

import React, { useCallback, useState } from 'react';
import { format } from 'date-fns';
import { faRoute } from '@fortawesome/pro-solid-svg-icons';
import { Checkbox } from 'src/components/PulseUI/Checkbox';
import { Dialog } from 'src/components/PulseUI/Dialog';
import { PulseButton } from 'src/components/PulseUI/PulseButton';
import {
  useCreateRouteMutation,
  useCreateSetRouteStopsMutation,
  useDeleteRouteMutation,
  useGetAllRoutesQuery,
  useLazyGetAllStrategiesMembersQuery,
  useLazyGetEntityDetailsByIdQuery,
} from 'src/api/redux';
import { MAX_ROUTE_STOPS } from 'src/api/redux/routes/constants';
import type { AllStrategiesMembersParams } from 'src/api/redux/strategy/allStrategiesApi';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import useRouteActions from 'src/hooks/routes/useRouteActions';
import useRouteOptimize from 'src/hooks/routes/useRouteOptimize';
import { buildRouteLabel } from 'src/hooks/routes/utils';
import { ROUTES_DRAWER_ID } from 'src/components/Routes/panels/RoutesDrawer/constants';
import {
  ADDRESS_LOOKUP_BATCH_SIZE,
  getSkippedNotice,
  UNORDERED_NOTICE,
} from './constants';

export interface CreateRouteFromResultsButtonProps {
  /** The query behind the visible map results (filters and map bounds) */
  queryParams: Partial<AllStrategiesMembersParams>;
  className?: string;
}

interface CreatedRoute {
  id: number;
  label: string;
  notice: string;
}

interface VisibleResults {
  providerIds: string[];
  total: number;
}

/**
 * Builds a route from the providers visible on the map: fetches up to
 * MAX_ROUTE_STOPS of them, resolves each one's primary address, creates the
 * route, optimizes it from the user's location (non-fatal) and opens it.
 */
const CreateRouteFromResultsButton: React.FC<
  CreateRouteFromResultsButtonProps
> = ({ queryParams, className }) => {
  const { openDrawer } = useDrawer();
  const { startView } = useRouteActions();
  const { optimize } = useRouteOptimize();
  const { data: routes } = useGetAllRoutesQuery();
  const [fetchMembers] = useLazyGetAllStrategiesMembersQuery();
  const [fetchEntityDetails] = useLazyGetEntityDetailsByIdQuery();
  const [createRoute] = useCreateRouteMutation();
  const [setRouteStops] = useCreateSetRouteStopsMutation();
  const [deleteRoute] = useDeleteRouteMutation();

  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [visible, setVisible] = useState<VisibleResults | null>(null);
  const [roundTrip, setRoundTrip] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when the route exists but the user should read a notice before opening it
  const [created, setCreated] = useState<CreatedRoute | null>(null);

  const handleOpen = useCallback(async () => {
    setError(null);
    setIsLoadingResults(true);
    try {
      const response = await fetchMembers({
        ...queryParams,
        page: 1,
        pageSize: MAX_ROUTE_STOPS,
      }).unwrap();
      const providerIds = [
        ...new Set(
          (response?.results ?? [])
            .map((member) => member.provider_id ?? member.id)
            .filter(Boolean)
            .map(String)
        ),
      ];
      setVisible({
        providerIds,
        total: response?.count ?? providerIds.length,
      });
    } catch {
      // API failures are already reported by the API layer
      setError("Couldn't load the results on the map.");
      setVisible({ providerIds: [], total: 0 });
    } finally {
      setIsLoadingResults(false);
    }
  }, [fetchMembers, queryParams]);

  const handleClose = useCallback(() => {
    if (isCreating) return;
    setVisible(null);
    setError(null);
    setCreated(null);
  }, [isCreating]);

  const openRoute = useCallback(
    (route: { id: number; label: string }) => {
      startView({ routeId: String(route.id), label: route.label, stops: [] });
      openDrawer(ROUTES_DRAWER_ID);
      setVisible(null);
      setCreated(null);
    },
    [startView, openDrawer]
  );

  const handleCreate = useCallback(async () => {
    if (created) {
      openRoute(created);
      return;
    }
    if (!visible || visible.providerIds.length === 0) return;
    setError(null);
    setIsCreating(true);

    try {
      // Stops need an address_key, so resolve each provider's primary address
      const stops: { provider_id: string; address_key: string }[] = [];
      for (
        let start = 0;
        start < visible.providerIds.length;
        start += ADDRESS_LOOKUP_BATCH_SIZE
      ) {
        const batch = visible.providerIds.slice(
          start,
          start + ADDRESS_LOOKUP_BATCH_SIZE
        );
        const results = await Promise.allSettled(
          batch.map((id) => fetchEntityDetails({ id }).unwrap())
        );
        results.forEach((result, index) => {
          const addressKey =
            result.status === 'fulfilled'
              ? result.value?.primary_address?.address_key
              : null;
          if (addressKey) {
            stops.push({ provider_id: batch[index], address_key: addressKey });
          }
        });
      }

      if (stops.length === 0) {
        setError('None of these providers have an address that can be routed.');
        return;
      }

      const label = buildRouteLabel(
        format(new Date(), 'MMM d'),
        (routes ?? []).map((route) => route.label)
      );
      const route = await createRoute({ label }).unwrap();
      try {
        await setRouteStops({
          routeId: route.id,
          stops: stops.map((stop, position) => ({ ...stop, position })),
        }).unwrap();
      } catch (stopsError) {
        // Don't leave an empty route behind; a retry would create a second one
        await deleteRoute(route.id)
          .unwrap()
          .catch(() => undefined);
        throw stopsError;
      }

      // Optimizing is best effort: the route keeps its stops if it fails
      const isOrdered =
        stops.length < 2 ||
        (await optimize({ routeId: route.id, roundtrip: roundTrip })) !== null;
      const skipped = visible.providerIds.length - stops.length;
      const notices = [
        skipped > 0 ? getSkippedNotice(skipped) : null,
        isOrdered ? null : UNORDERED_NOTICE,
      ].filter(Boolean);

      if (notices.length > 0) {
        setCreated({ id: route.id, label: route.label, notice: notices.join(' ') });
        return;
      }
      openRoute(route);
    } catch {
      // API failures are already reported by the API layer
      setError("Couldn't create the route.");
    } finally {
      setIsCreating(false);
    }
  }, [
    visible,
    created,
    routes,
    roundTrip,
    fetchEntityDetails,
    createRoute,
    setRouteStops,
    deleteRoute,
    optimize,
    openRoute,
  ]);

  const isCapped = !!visible && visible.total > MAX_ROUTE_STOPS;
  const routedCount = visible
    ? Math.min(visible.providerIds.length, MAX_ROUTE_STOPS)
    : 0;

  return (
    <>
      <PulseButton
        variant="outline"
        color="primary"
        size="md"
        className={className}
        onClick={handleOpen}
        loading={isLoadingResults}
        disabled={isLoadingResults}
        startDecorator={faRoute}
        aria-label="Create route from visible results"
        data-testid="create-route-from-results-button"
      >
        Create Route
      </PulseButton>

      {visible && (
        <Dialog
          isOpen
          onClose={handleClose}
          size="lg"
          aria-labelledby="create-route-from-results-title"
          data-testid="create-route-from-results-dialog"
        >
          <Dialog.Header>
            <h2
              className="text-lg font-medium"
              id="create-route-from-results-title"
            >
              Create Route from Map
            </h2>
          </Dialog.Header>
          <Dialog.Content>
            <div className="flex flex-col gap-3 p-4 text-sm text-neutral-700">
              {created ? (
                <p role="alert" data-testid="create-route-notice">
                  Route created. {created.notice}
                </p>
              ) : visible.providerIds.length === 0 ? (
                <p role="alert" data-testid="create-route-empty">
                  {error ?? 'There are no results on the map to route.'}
                </p>
              ) : (
                <>
                  <p data-testid="create-route-summary">
                    {visible.total} {visible.total === 1 ? 'result is' : 'results are'}{' '}
                    visible on the map. Stops are ordered to drive from your
                    current location.
                  </p>
                  {isCapped && (
                    <p role="alert" data-testid="create-route-cap-warning">
                      Routes hold up to {MAX_ROUTE_STOPS} stops, so only the
                      first {MAX_ROUTE_STOPS} results will be added.
                    </p>
                  )}
                  <Checkbox
                    checked={roundTrip}
                    onChange={(e) => setRoundTrip(e.target.checked)}
                    label="Round trip (return to the starting location)"
                    size="sm"
                    data-testid="create-route-round-trip"
                  />
                  {error && (
                    <p className="text-xs text-red-600" role="alert">
                      {error}
                    </p>
                  )}
                </>
              )}
            </div>
          </Dialog.Content>
          <Dialog.Actions>
            <PulseButton
              variant="outline"
              onClick={handleClose}
              disabled={isCreating}
              className="w-full rounded-full"
              data-testid="create-route-cancel"
            >
              Cancel
            </PulseButton>
            <PulseButton
              variant="solid"
              color="primary"
              onClick={handleCreate}
              loading={isCreating}
              disabled={isCreating || visible.providerIds.length === 0}
              className="w-full rounded-full"
              data-testid="create-route-confirm"
            >
              {created
                ? 'Open Route'
                : routedCount > 0
                  ? `Create Route (${routedCount})`
                  : 'Create Route'}
            </PulseButton>
          </Dialog.Actions>
        </Dialog>
      )}
    </>
  );
};

export default CreateRouteFromResultsButton;
